/**
 * 表示言語（TM-674）。
 *
 * 既定はブラウザの言語設定で、アカウントメニューで選んだ言語はブラウザ（localStorage）に保存する。
 * ユーザーやサーバーには保存しない（表示の設定はブラウザに任せ、システム側で既定言語を持たない）。
 * そのため別のブラウザでは選び直しになり、共用端末では前の利用者が選んだ言語が残る（仕様）。
 * 表示だけの設定なので、API が返すメッセージやメールの言語は変わらない。
 */

export const SUPPORTED_LOCALES = ['ja', 'en'] as const

export type Locale = typeof SUPPORTED_LOCALES[number]

/** 既定ロケール。ブラウザの言語設定が対応言語でないときに使う。 */
export const DEFAULT_LOCALE: Locale = 'ja'

/** 選択肢の表示名。どの言語で表示中でも読めるよう、各言語の自称で出す。 */
export const LOCALE_NATIVE_NAMES: Record<Locale, string> = {
  ja: '日本語',
  en: 'English'
}

const STORAGE_KEY = 'jizaipad.locale'

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)

/** ブラウザに保存した言語。無い・読めない・未対応の値なら null。 */
export const readStoredLocale = (): Locale | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    return isLocale(value) ? value : null
  } catch {
    return null
  }
}

/**
 * ブラウザの言語設定（`navigator.languages`）から、対応している最初の言語を返す。
 * `en-US` / `ja_JP` のような地域付きのタグは先頭の言語部分で判定する。対応言語が無ければ null。
 */
export const detectBrowserLocale = (languages: readonly string[]): Locale | null => {
  for (const tag of languages) {
    const primary = tag.toLowerCase().split(/[-_]/)[0]
    if (isLocale(primary)) return primary
  }
  return null
}

export const storeLocale = (locale: Locale): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // プライベートブラウズ等で保存できなくても表示の切り替えは続ける。
  }
}
